import { useState } from "react";
import { Link } from "react-router-dom";
import DramaCard from "@/components/feature/DramaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { specials } from "@/mocks/specials";
import { getDramasByIds } from "@/utils/media";

export default function Specials() {
  usePageMeta({
    title: "专题合集 - 韩剧TV",
    description:
      "韩剧TV专题合集为你策划主题片单，包括年度最佳韩剧、秋季新剧速递、烧脑悬疑合集与下饭甜宠精选，跟着专题追剧不迷路。",
    keywords: "韩剧专题,韩剧片单,韩剧合集,年度韩剧,新剧推荐",
  });

  const [activeId, setActiveId] = useState(specials[0].id);
  const active = specials.find((s) => s.id === activeId) ?? specials[0];
  const related = specials.filter((s) => s.id !== active.id);

  return (
    <div className="w-full">
      {/* 头图 */}
      <section className="relative h-[300px] w-full overflow-hidden md:h-[400px]">
        <img src={active.cover} alt={`${active.title} 专题封面`} title={`${active.title} - 韩剧TV专题`} className="h-full w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-4 md:px-8">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs text-background-50">
              <i className="ri-stack-line"></i> 专题策划
            </span>
            <h1 className="mt-3 max-w-2xl font-heading text-2xl font-800 text-background-50 md:text-4xl">{active.title}</h1>
            <p className="mt-1 font-label text-sm text-primary-200">{active.subtitle}</p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/85 md:text-base">{active.intro}</p>
          </div>
        </div>
      </section>

      <div className="w-full px-4 py-8 md:px-8 md:py-12">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "专题合集" }]} />

        {/* 专题切换 */}
        <div className="mt-8 flex flex-wrap gap-3">
          {specials.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setActiveId(s.id)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-sm transition-colors cursor-pointer ${
                s.id === activeId
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              <i className="ri-price-tag-3-line"></i>
              {s.title}
            </button>
          ))}
        </div>

        {/* 分组内容 */}
        <div className="mt-10 space-y-12">
          {active.groups.map((group) => {
            const groupDramas = getDramasByIds(group.dramaIds);
            return (
              <section key={group.title}>
                <div className="flex items-end justify-between gap-4 border-b border-background-200/70 pb-4">
                  <div>
                    <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">{group.title}</h2>
                    <p className="mt-1 text-sm text-foreground-500">{group.desc}</p>
                  </div>
                  <span className="shrink-0 font-label text-sm text-foreground-400">{groupDramas.length} 部</span>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-4 md:gap-5 lg:grid-cols-3">
                  {groupDramas.map((d) => (
                    <DramaCard key={d.id} drama={d} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* 相关专题 */}
        <section className="mt-14 border-t border-background-200/70 pt-10">
          <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">相关专题推荐</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            {related.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setActiveId(s.id);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="group flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-50 text-left transition-colors hover:border-primary-200 cursor-pointer"
              >
                <div className="aspect-[16/9] w-full overflow-hidden bg-background-200">
                  <img src={s.cover} alt={`${s.title} 专题封面`} title={`${s.title} - 韩剧TV专题`} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base font-600 text-foreground-950 group-hover:text-primary-600">{s.title}</h3>
                  <p className="mt-1 font-label text-xs text-foreground-500">{s.subtitle}</p>
                  <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{s.intro}</p>
                </div>
              </button>
            ))}
          </div>
        </section>

        <div className="mt-12 flex justify-center">
          <Link to="/category/drama" className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-8 py-3 text-sm font-500 text-background-50 hover:bg-primary-600 cursor-pointer">
            <i className="ri-apps-2-line"></i> 查看全部剧集
          </Link>
        </div>
      </div>
    </div>
  );
}